import type { FC } from 'react'

import { Input, Radio, RadioGroup, Select } from '@skytech/ui'
import { Button } from '@skytech/ui/Button'
import { COUNTRY_FLAGS, countryOptions, getDefaultCountryId, isOnlyLetters } from '@skytech/utils'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import type { FieldProps } from 'formik'
import { Field, Form, Formik } from 'formik'

import { useAppContext } from '@/app/index.context'
import { Translation } from '@/app/translations/Translation'
import { useTranslation } from '@/app/translations/translations-hooks'
import { useAdminAccess } from '@/hooks/access'
import queries from '@/modules/admin/queries'
import type { Address } from '@/modules/admin/resources'
import adminResources from '@/modules/admin/resources'

export const AddressModal: FC<{
  addressDetails: Address | null
  addressList: Address[]
  onClose: () => void
}> = ({ addressDetails, addressList, onClose }) => {
  useAdminAccess()

  const { locale } = useAppContext()

  const translate = useTranslation()
  const queryClient = useQueryClient()

  const { data: customers = [] } = useQuery(
    queries.adminUserCustomersList(),
    adminResources.getUserCustomersList
  )

  const defaultPhoneCountryId = getDefaultCountryId(locale)
  const initialIsGlobalValues = addressDetails?.isGlobal ? 'global' : 'local'

  const createAddressMutation = useMutation(adminResources.postAddress, {
    onSuccess: address => {
      const customerName = customers.find(customer => customer.id === address.customerId)?.name
      const newAddress = { ...address, customerName }
      queryClient.setQueryData(queries.adminAddresses(), [...addressList, newAddress])
      onClose()
    },
  })

  const updateAddressMutation = useMutation(adminResources.updateAddress, {
    onSuccess: address => {
      const updatedAddresses = addressDetails
        ? addressList.filter(({ id }) => addressDetails.id !== id)
        : addressList
      const countryName = countryOptions.find(country => country.value === address.countryId)?.label
      const newAddress = { ...address, countryName: countryName }
      queryClient.setQueryData(queries.adminAddresses(), [...updatedAddresses, newAddress])
      onClose()
    },
  })

  const deleteAddressMutation = useMutation(adminResources.deleteAddress, {
    onSuccess() {
      queryClient.invalidateQueries(queries.adminAddresses())
      onClose()
    },
  })

  const isSaving = createAddressMutation.isLoading || updateAddressMutation.isLoading

  return (
    <Formik
      initialValues={{
        address: addressDetails?.address || '',
        city: addressDetails?.city || '',
        zip: addressDetails?.zip || '',
        countryId: addressDetails?.countryId || defaultPhoneCountryId,
        customerId: addressDetails?.customerId || '',
        isGlobal: addressDetails ? initialIsGlobalValues : 'global',
      }}
      validate={values => {
        const errors: Partial<Record<keyof Address, string> & { customerId: string }> = {}

        if (!values.countryId) errors.countryId = translate('This field is mandatory')
        if (!values.address) errors.address = translate('This field is mandatory')
        if (!values.city) errors.city = translate('This field is mandatory')
        if (values.city && !isOnlyLetters(values.city)) {
          errors.city = translate('City name may only consist of letters')
        }
        if (!values.zip) errors.zip = translate('This field is mandatory')
        // Customer ID cannot be updated
        if (!values.customerId && values.isGlobal === 'local') {
          const customerFilteredBySelectedCountry = customers.filter(
            customer => customer.countryId === values.countryId
          )
          errors.customerId =
            customerFilteredBySelectedCountry.length === 0
              ? translate(
                  'No customers found for the selected country. Please consider changing the address type to GLOBAL or selecting another country.'
                )
              : translate('This field is mandatory if you choose the address to be LOCAL')
        }
        if (!values.isGlobal) errors.isGlobal = translate('This field is mandatory')

        return errors
      }}
      onSubmit={formValues => {
        const isAddressGlobal = Boolean(formValues.isGlobal === 'global')
        const addressPayload = {
          countryId: formValues.countryId,
          customerId: isAddressGlobal ? null : Number(formValues.customerId),
          address: formValues.address,
          zip: formValues.zip,
          city: formValues.city,
        }

        // For updating
        if (addressDetails?.id) {
          updateAddressMutation.mutate({ id: addressDetails.id, data: addressPayload })
          return
        }

        createAddressMutation.mutate(addressPayload)
      }}
    >
      {({ values }) => {
        const customerFilteredBySelectedCountry = addressDetails?.customerId
          ? customers
          : customers.filter(customer => customer.countryId === values.countryId)

        return (
          <div>
            <h2>
              <Translation id={addressDetails ? 'Edit address' : 'Add address'} />
            </h2>

            <Form>
              <Field name="countryId">
                {({ field, meta, form }: FieldProps) => (
                  <Select
                    capitalizeLabel
                    id="countryId"
                    label={<Translation id="Country" />}
                    options={countryOptions.map(option => ({
                      value: option.value,
                      label: `${COUNTRY_FLAGS[option.label]} ${option.label}`,
                    }))}
                    marginBottom={16}
                    touched={meta.touched}
                    error={meta.error}
                    // Country cannot be updated if there is a company selected for the address
                    isDisabled={Boolean(addressDetails?.customerId) && values.isGlobal === 'local'}
                    {...field}
                    onChange={option => {
                      form.setFieldValue(field.name, option?.value)
                      form.setFieldValue('customerId', null)
                      form.setFieldTouched('customerId')
                      if (customerFilteredBySelectedCountry.length === 0) {
                        form.setFieldError(
                          'customerId',
                          translate(
                            'No customers found for the selected country. Please consider changing the address type to GLOBAL or selecting another country.'
                          )
                        )
                      }
                    }}
                  />
                )}
              </Field>

              <Field name="address">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    id="address"
                    capitalizeLabel
                    label={
                      <>
                        <Translation id="Address" />*
                      </>
                    }
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Field name="zip">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    id="zip"
                    label={
                      <>
                        <Translation id="Zip" />*
                      </>
                    }
                    maxLength={10}
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <Field name="city">
                {({ field, meta }: FieldProps) => (
                  <Input
                    isFullWidth
                    id="city"
                    label={
                      <>
                        <Translation id="City" />*
                      </>
                    }
                    marginBottom={16}
                    error={meta.error}
                    touched={meta.touched}
                    {...field}
                  />
                )}
              </Field>

              <RadioGroup
                id="address-type"
                label={
                  <>
                    <Translation id="Type" />*
                  </>
                }
              >
                <Field name="isGlobal">
                  {({ field, form }: FieldProps) => (
                    <Radio
                      capitalizeLabel
                      id="global"
                      label={
                        <div className="flex items-center">
                          <Translation id="Global" />
                        </div>
                      }
                      marginBottom={16}
                      {...field}
                      onChange={() => {
                        form.setFieldValue(field.name, 'global')
                        // No customer reselection in edit mode
                        if (addressDetails?.id) return
                        form.setFieldValue('customerId', null)
                      }}
                    />
                  )}
                </Field>

                <Field name="isGlobal">
                  {({ field, form }: FieldProps) => (
                    <Radio
                      capitalizeLabel
                      id="local"
                      label={
                        <div className="flex items-center">
                          <Translation id="Local" />
                        </div>
                      }
                      isDisabled={Boolean(addressDetails?.id && !addressDetails?.isGlobal)}
                      marginBottom={16}
                      {...field}
                      onChange={() => {
                        form.setFieldValue(field.name, 'local')

                        form.setFieldValue('customerId', null)
                        form.setFieldTouched('customerId')
                        if (customerFilteredBySelectedCountry.length === 0) {
                          form.setFieldError(
                            'customerId',
                            translate(
                              'No customers found for the selected country. Please consider changing the address type to GLOBAL or selecting another country.'
                            )
                          )
                        }
                      }}
                    />
                  )}
                </Field>
              </RadioGroup>

              {values.isGlobal === 'local' && (
                <Field name="customerId">
                  {({ field, meta, form }: FieldProps) => (
                    <Select
                      className="mt-4"
                      capitalizeLabel
                      id="customerId"
                      label={
                        <>
                          <Translation id="Customer" />*
                        </>
                      }
                      options={customerFilteredBySelectedCountry.map(option => ({
                        value: option.id,
                        label: option.name,
                      }))}
                      marginBottom={16}
                      touched={meta.touched}
                      error={meta.error}
                      {...field}
                      // Customer ID cannot be updated as long as the address does not become global
                      isDisabled={Boolean(
                        addressDetails?.customerId && values.isGlobal === 'local'
                      )}
                      onChange={option => {
                        form.setFieldValue(field.name, option?.value)
                      }}
                    />
                  )}
                </Field>
              )}

              <footer className="mt-4 flex items-center justify-end gap-4">
                <div className="flex items-center">
                  <Button variant="tertiary" disabled={isSaving} onClick={onClose}>
                    <Translation id="Cancel" />
                  </Button>

                  {addressDetails && (
                    <Button
                      variant="danger"
                      isLoading={deleteAddressMutation.isLoading}
                      onClick={() => deleteAddressMutation.mutate(addressDetails.id)}
                    >
                      <Translation id="Delete" />
                    </Button>
                  )}
                </div>
                <Button variant="primary" type="submit" isLoading={isSaving}>
                  <Translation id="Submit" />
                </Button>
              </footer>
            </Form>
          </div>
        )
      }}
    </Formik>
  )
}
